import { getModeRouteKind, ROUTE_KINDS } from './serviceModeFields';
import { getServiceMode, getServiceAddress } from './serviceModes';

/**
 * Risoluzione della "tratta" di un ordine in punti da mostrare, unica per tutte
 * le viste read-only (celle tabella, card, modali, mappe, export).
 *
 * La forma dipende dalla modalità di servizio (`serviceModeFields.ROUTE_KINDS`):
 * - `single_point` (assistenza): UN solo punto — il luogo del servizio, sul
 *   campo `indirizzo` (fallback su `indirizzo_da` per gli ordini legacy, vedi
 *   `getServiceAddress`). Mai una freccia verso il nulla né un trattino di
 *   arrivo penzolante.
 * - `stations` (treno): partenza/arrivo sono stazioni. `station_from`/`station_to`
 *   con fallback sugli indirizzi, che gli order creati dal booking rispecchiano
 *   (vedi `bookingLegPayload.buildLegPayload`) ma quelli legacy no.
 * - `two_points` (trasporto, ambulanza, oggetti): comportamento storico.
 *
 * Ritorna `{ kind, points: [{ address, icon, labelKey }] }`, con `icon` chiave
 * del registro semantico (`constants/icons`). Un punto senza indirizzo resta
 * nell'elenco con `address` vuoto: sta al presentatore decidere il placeholder.
 */
export function getRoutePoints(order = {}) {
  const kind = getModeRouteKind(getServiceMode(order));

  if (kind === ROUTE_KINDS.SINGLE_POINT) {
    return {
      kind,
      points: [{ address: getServiceAddress(order), icon: 'location', labelKey: 'luogo' }],
    };
  }

  if (kind === ROUTE_KINDS.STATIONS) {
    return {
      kind,
      points: [
        {
          address: order.station_from || order.indirizzo_da || '',
          icon: 'train',
          labelKey: 'stazione_partenza',
        },
        {
          address: order.station_to || order.indirizzo_a || '',
          icon: 'destination',
          labelKey: 'stazione_arrivo',
        },
      ],
    };
  }

  return {
    kind,
    points: [
      { address: order.indirizzo_da || '', icon: 'circleDot', labelKey: 'indirizzo_partenza' },
      { address: order.indirizzo_a || '', icon: 'destination', labelKey: 'indirizzo_arrivo' },
    ],
  };
}

/** `true` quando la tratta è un punto singolo (nessuna freccia, nessun arrivo). */
export const isSinglePointRoute = (order) =>
  getRoutePoints(order).kind === ROUTE_KINDS.SINGLE_POINT;

/**
 * Indirizzi della tratta come coppia piatta `{ from, to }` — `to` è `null` per
 * il punto singolo. Per i chiamanti che devono ancora ragionare in due colonne
 * (export, righe di tabella pre-mappate, chiavi di raggruppamento).
 */
export function getRouteAddresses(order) {
  const { points } = getRoutePoints(order);
  return { from: points[0]?.address || '', to: points[1]?.address ?? null };
}
